// Sistemdeki tüm bildirim e-postalarının (kayıt, talep, pasiflik, hesap) içeriklerini düzenleme kartı
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { ChevronDown, Loader2, MailCheck } from 'lucide-react';
import { TemplateEditor } from './ReminderSettingsCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TemplateItem = ({ tpl, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState({ [`${tpl.key}_subject`]: tpl.subject, [`${tpl.key}_body`]: tpl.body });
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(tpl.enabled);
  const toggle = async (v) => {
    setEnabled(v);
    try {
      const res = await axios.put(`${API}/settings/email-templates/${tpl.key}/enabled`, { enabled: v });
      toast.success(`"${tpl.label}": ${res.data.message}`);
    } catch {
      setEnabled(!v);
      toast.error('Değiştirilemedi');
    }
  };
  const defaults = { [`${tpl.key}_subject`]: tpl.default_subject, [`${tpl.key}_body`]: tpl.default_body };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/email-templates/${tpl.key}`, { subject: values[`${tpl.key}_subject`], body: values[`${tpl.key}_body`] });
      toast.success(`"${tpl.label}" e-postası kaydedildi`);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kaydedilemedi');
    } finally { setSaving(false); }
  };

  return (
    <div className={`border rounded-lg ${enabled ? '' : 'bg-slate-50'}`} data-testid={`system-template-${tpl.key}`}>
      <div className="flex items-center gap-3 px-3">
      <Switch checked={enabled} onCheckedChange={toggle} title={enabled ? 'E-posta açık' : 'E-posta kapalı'} data-testid={`system-template-enabled-${tpl.key}`} />
      <button type="button" onClick={() => setOpen(o => !o)} className="flex-1 min-w-0 flex items-center gap-3 py-2.5 text-left" data-testid={`system-template-toggle-${tpl.key}`}>
        <div className="min-w-0 flex-1">
          <p className={`text-sm font-medium ${enabled ? 'text-slate-800' : 'text-slate-400 line-through'}`}>{tpl.label}</p>
          <p className="text-xs text-slate-500 truncate">Alıcı: {tpl.to} · Konu: {values[`${tpl.key}_subject`]}</p>
        </div>
        {!enabled && <Badge className="bg-slate-200 text-slate-600" data-testid={`system-template-off-${tpl.key}`}>Kapalı</Badge>}
        {tpl.customized && <Badge className="bg-blue-50 text-blue-700 border-blue-200" data-testid={`system-template-custom-${tpl.key}`}>Özelleştirildi</Badge>}
        <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      </div>
      {open && (
        <div className="px-3 pb-3 pt-1 space-y-3 border-t">
          <TemplateEditor label="E-posta içeriği" prefix={tpl.key} vars={tpl.vars} templates={values} defaults={defaults} onChange={(p) => setValues(v => ({ ...v, ...p }))} />
          <div className="flex justify-end">
            <Button size="sm" onClick={save} disabled={saving} data-testid={`system-template-save-${tpl.key}`}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Kaydet
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export const SystemEmailTemplatesCard = () => {
  const [templates, setTemplates] = useState(null);
  const load = () => axios.get(`${API}/settings/email-templates`).then(r => setTemplates(r.data)).catch(() => setTemplates([]));
  useEffect(() => { load(); }, []);

  const groups = (templates || []).reduce((acc, t) => ({ ...acc, [t.group]: [...(acc[t.group] || []), t] }), {});

  return (
    <Card className="border-slate-200" data-testid="system-email-templates-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><MailCheck className="h-5 w-5" />Sistem E-postaları</CardTitle>
        <CardDescription>Sistemin otomatik gönderdiği tüm bildirim e-postaları. Anahtarla istemediğiniz bildirimi tamamen kapatabilir; konu ve içerikleri düzenleyebilir, önizleyebilir ve test e-postası gönderebilirsiniz.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {templates === null && <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>}
        {Object.entries(groups).map(([group, items]) => (
          <div key={group} className="space-y-2" data-testid={`system-template-group-${group}`}>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{group} <span className="font-normal normal-case">({items.length})</span></p>
            {items.map(t => <TemplateItem key={t.key} tpl={t} onSaved={load} />)}
          </div>
        ))}
      </CardContent>
    </Card>
  );
};
